import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button,
  Typography, InputAdornment, Alert,
  CircularProgress, Stepper, Step, StepLabel,
  Select, MenuItem, FormControl, InputLabel,
  Checkbox, FormControlLabel, Divider
} from '@mui/material';
import {
  PhoneAndroid, Lock, Person, Home,
  Business, CheckCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  registerSendOtp,
  registerVerifyOtp,
  registerComplete
} from '../api/authApi';
import { useApp } from '../context/AppContext';

const steps = ['Personal Details', 'Center Details', 'Center Settings'];

const STATES = [
  { id: 1,  name: 'Maharashtra' },
  { id: 2,  name: 'Gujarat' },
  { id: 3,  name: 'Punjab' },
  { id: 4,  name: 'Uttar Pradesh' },
  { id: 5,  name: 'Rajasthan' },
  { id: 6,  name: 'Madhya Pradesh' },
  { id: 7,  name: 'Karnataka' },
  { id: 8,  name: 'Tamil Nadu' },
  { id: 9,  name: 'Andhra Pradesh' },
  { id: 10, name: 'Telangana' },
  { id: 11, name: 'Haryana' },
  { id: 12, name: 'Bihar' },
  { id: 13, name: 'West Bengal' },
  { id: 14, name: 'Odisha' },
  { id: 15, name: 'Assam' },
  { id: 16, name: 'Chhattisgarh' },
  { id: 17, name: 'Jharkhand' },
  { id: 18, name: 'Uttarakhand' },
  { id: 19, name: 'Himachal Pradesh' },
  { id: 20, name: 'Goa' },
];

export default function Register() {
  const { theme } = useApp();
  const navigate        = useNavigate();
  const [activeStep,    setActiveStep]    = useState(0);
  const [otpSent,       setOtpSent]       = useState(false);
  const [otpVerified,   setOtpVerified]   = useState(false);
  const [devOtp,        setDevOtp]        = useState('');
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');
  const [termsAccepted, setTermsAccepted] = useState(true);

  // ── Step 1 — Personal ────────────────────────────
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [mobile,    setMobile]    = useState('');
  const [otp,       setOtp]       = useState('');
  const [password,  setPassword]  = useState('');
  const [referral,  setReferral]  = useState('');

  // ── Step 2 — Center Details ──────────────────────
  const [centerName, setCenterName] = useState('');
  const [village,    setVillage]    = useState('');
  const [taluka,     setTaluka]     = useState('');
  const [district,   setDistrict]   = useState('');
  const [stateId,    setStateId]    = useState('');
  const [pinCode,    setPinCode]    = useState('');

  // ── Step 3 — Center Settings ─────────────────────
  const [collectionTypeId, setCollectionTypeId] = useState('');
  const [milkRateTypeId,   setMilkRateTypeId]   = useState('');
  const [shiftId,          setShiftId]           = useState('');
  const [paymentPeriodId,  setPaymentPeriodId]   = useState('');

  // ── Send OTP ─────────────────────────────────────
  const handleSendOtp = async () => {
    setError('');
    if (!firstName)                      { setError('Please enter first name.');              return; }
    if (!lastName)                       { setError('Please enter last name.');               return; }
    if (!mobile || mobile.length !== 10) { setError('Please enter valid 10-digit mobile.');   return; }
    if (!termsAccepted)                  { setError('Please accept Terms & Conditions.');     return; }

    setLoading(true);
    try {
      const res = await registerSendOtp({ mobileNumber: mobile });
      if (res.data.success) {
        setOtpSent(true);
        setDevOtp(res.data.message);
      } else {
        setError(res.data.message);
      }
    } catch {
      setError('Cannot connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Verify OTP ───────────────────────────────────
  const handleVerifyOtp = async () => {
    setError('');
    if (!otp || otp.length < 4)           { setError('Please enter the OTP.');                      return; }
    if (!password || password.length < 4) { setError('Password must be at least 4 characters.');    return; }

    setLoading(true);
    try {
      const res = await registerVerifyOtp({ mobileNumber: mobile, otpCode: otp });
      if (res.data.success) {
        setOtpVerified(true);
        setActiveStep(1);
      } else {
        setError(res.data.message);
      }
    } catch {
      setError('Cannot connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2 Next ──────────────────────────────────
  const handleStep2Next = () => {
    setError('');
    if (!centerName) { setError('Please enter dairy name.'); return; }
    if (!stateId)    { setError('Please select state.');     return; }
    setActiveStep(2);
  };

  // ── Complete Registration ─────────────────────────
  const handleComplete = async () => {
    setError('');
    if (!collectionTypeId) { setError('Please select Collection Type.'); return; }
    if (!milkRateTypeId)   { setError('Please select Milk Type.');       return; }
    if (!shiftId)          { setError('Please select Collection Shift.'); return; }
    if (!paymentPeriodId)  { setError('Please select Payment Period.');  return; }

    setLoading(true);
    try {
      const res = await registerComplete({
        firstName,
        lastName,
        mobileNumber:     mobile,
        password,
        referralCode:     referral || null,
        langId:           1,
        centerName,
        village,
        taluka,
        district,
        stateId:          parseInt(stateId),
        pinCode,
        collectionTypeId: parseInt(collectionTypeId),
        milkRateTypeId:   parseInt(milkRateTypeId),
        shiftId:          parseInt(shiftId),
        paymentPeriodId:  parseInt(paymentPeriodId)
      });

      if (res.data.success) {
        setActiveStep(3);
      } else {
        setError(res.data.message);
      }
    } catch {
      setError('Cannot connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Shared Styles ────────────────────────────────
  const inputSx = { mb: 2 };

  const btnSx = {
    background:   `linear-gradient(135deg,${theme.primary},${theme.hover})`,
    borderRadius: 2,
    px:           3,
    fontWeight:   700,
    '&:hover':    { background: `linear-gradient(135deg,${theme.dark},${theme.primary})` }
  };

  const backBtnSx = {
    borderRadius: 2,
    color:        theme.primary,
    borderColor:  theme.primary
  };

  return (
    <Box sx={{
      minHeight:      '100vh',
      background:     'linear-gradient(135deg, #f0f7f4 0%, #e8f5e9 100%)',
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      p:              2,
      py:             4
    }}>

      {/* ── Logo ── */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{
          fontWeight: 800,
          color:      theme.primary,
          fontFamily: 'Poppins, sans-serif'
        }}>
          🐄 DairyPro
        </Typography>
        <Typography variant="h6" sx={{ color: '#555', mt: 0.5 }}>
          Create an Account
        </Typography>
      </Box>

      {/* ── Main Card ── */}
      <Card sx={{
        width:        '100%',
        maxWidth:     900,
        borderRadius: 3,
        boxShadow:    '0 4px 24px rgba(26,122,74,0.10)',
        overflow:     'hidden'
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            display:             'grid',
            gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }
          }}>

            {/* ════════════════════════════════════
                LEFT — Plans Panel
            ════════════════════════════════════ */}
            <Box sx={{
              background:  'linear-gradient(160deg, #f0fdf4, #ecfdf5)',
              p:           { xs: 3, md: 4 },
              borderRight: { md: '1px solid #e0e0e0' }
            }}>
              <Typography variant="subtitle1" sx={{
                fontWeight: 800,
                color:      theme.primary,
                mb:         2
              }}>
                💰 Application Charges
              </Typography>

              {[
                {
                  name:     '7 Days Trial',
                  price:    '₹0',
                  badge:    'FREE',
                  features: ['All standard features', '7 days free access']
                },
                {
                  name:     'Standard Plan — 1 Year',
                  price:    '₹2,500',
                  features: ['Basic Features', 'Farmer Mgmt', 'Milk Entry', 'Payments']
                },
                {
                  name:     'Advance Plan — 1 Year',
                  price:    '₹3,200',
                  features: [
                    'All Standard', 'Multi Ratechart',
                    'Milk Rejection', '200 Farmers',
                    'Hardware Auto', 'Multi Collection'
                  ]
                }
              ].map((plan, i) => (
                <Box key={i} sx={{
                  border:     `1.5px solid ${i === 0 ? theme.hover : '#e0e0e0'}`,
                  borderRadius: 2,
                  p:          1.5,
                  mb:         1.5,
                  background: i === 0 ? theme.light : '#fff'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: theme.primary }}>
                      {plan.name}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: theme.primary }}>
                      {plan.price}
                    </Typography>
                  </Box>
                  {plan.badge && (
                    <Box sx={{
                      display:      'inline-block',
                      background:   theme.primary,
                      color:        '#fff',
                      fontSize:     '0.65rem',
                      fontWeight:   700,
                      px:           1,
                      borderRadius: 10,
                      mb:           0.5
                    }}>
                      {plan.badge}
                    </Box>
                  )}
                  <Box sx={{
                    display:             'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap:                 0.3
                  }}>
                    {plan.features.map((f, j) => (
                      <Typography key={j} variant="caption" sx={{ color: '#666' }}>
                        ✓ {f}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ))}

              {/* Note */}
              <Box sx={{
                mt:         2,
                p:          1.5,
                background: '#fff8f0',
                border:     '1px solid #fed7aa',
                borderRadius: 2
              }}>
                <Typography variant="caption" sx={{ color: '#9a3412' }}>
                  <strong>Note:</strong> This app is for Milk Collection
                  Dairy / Center owners only. If you are a farmer or milk
                  producer, this app is not for you.
                </Typography>
              </Box>
            </Box>

            {/* ════════════════════════════════════
                RIGHT — Form Panel
            ════════════════════════════════════ */}
            <Box sx={{ p: { xs: 3, md: 4 } }}>

              {/* Stepper */}
              {activeStep < 3 && (
                <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              {/* DEV OTP hint */}
              {devOtp && otpSent && !otpVerified && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontSize: '0.8rem' }}>
                  {devOtp}
                </Alert>
              )}

              {/* ════ STEP 1 : Personal Details ════ */}
              {activeStep === 0 && (
                <Box>
                  <TextField
                    fullWidth label="First Name" value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person sx={{ color: theme.primary }} />
                        </InputAdornment>
                      )
                    }}
                    sx={inputSx}
                  />

                  <TextField
                    fullWidth label="Last Name" value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person sx={{ color: theme.primary }} />
                        </InputAdornment>
                      )
                    }}
                    sx={inputSx}
                  />

                  <TextField
                    fullWidth label="Mobile Number" value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneAndroid sx={{ color: theme.primary }} />
                        </InputAdornment>
                      )
                    }}
                    sx={inputSx}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I agree to <strong>Terms & Conditions</strong>
                      </Typography>
                    }
                    sx={{ mb: 1 }}
                  />

                  {/* OTP + Password — shown after OTP sent */}
                  {otpSent && (
                    <Box sx={{ mt: 1 }}>
                      <Divider sx={{ mb: 2 }} />
                      <TextField
                        fullWidth label="Enter OTP" value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                        sx={inputSx}
                      />
                      <TextField
                        fullWidth label="Set Password" type="password" value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock sx={{ color: theme.primary }} />
                            </InputAdornment>
                          )
                        }}
                        sx={inputSx}
                      />
                      <TextField
                        fullWidth label="Referral Code (Optional)" value={referral}
                        onChange={(e) => setReferral(e.target.value)}
                        sx={inputSx}
                      />
                    </Box>
                  )}

                  <Box sx={{
                    display:     'flex',
                    justifyContent: 'space-between',
                    alignItems:  'center',
                    mt:          1
                  }}>
                    <Typography
                      variant="body2"
                      onClick={() => navigate('/login')}
                      sx={{
                        color:      theme.primary,
                        fontWeight: 700,
                        cursor:     'pointer',
                        '&:hover':  { textDecoration: 'underline' }
                      }}
                    >
                      ← Back to Login
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={otpSent ? handleVerifyOtp : handleSendOtp}
                      disabled={loading}
                      sx={btnSx}
                    >
                      {loading
                        ? <CircularProgress size={22} color="inherit" />
                        : otpSent ? 'Next →' : 'Request OTP'}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* ════ STEP 2 : Center Details ════ */}
              {activeStep === 1 && (
                <Box>
                  <TextField
                    fullWidth label="Dairy Name" value={centerName}
                    onChange={(e) => setCenterName(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business sx={{ color: theme.primary }} />
                        </InputAdornment>
                      )
                    }}
                    sx={inputSx}
                  />
                  <TextField
                    fullWidth label="Village" value={village}
                    onChange={(e) => setVillage(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Home sx={{ color: theme.primary }} />
                        </InputAdornment>
                      )
                    }}
                    sx={inputSx}
                  />
                  <TextField
                    fullWidth label="Taluka" value={taluka}
                    onChange={(e) => setTaluka(e.target.value)}
                    sx={inputSx}
                  />
                  <TextField
                    fullWidth label="District" value={district}
                    onChange={(e) => setDistrict(e.target.value)}
                    sx={inputSx}
                  />

                  <FormControl fullWidth sx={inputSx}>
                    <InputLabel>State</InputLabel>
                    <Select
                      value={stateId}
                      label="State"
                      onChange={(e) => setStateId(e.target.value)}
                    >
                      {STATES.map((s) => (
                        <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth label="Pin Code" value={pinCode}
                    onChange={(e) => setPinCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                    sx={inputSx}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Button variant="outlined" onClick={() => setActiveStep(0)} sx={backBtnSx}>
                      ← Back
                    </Button>
                    <Button variant="contained" onClick={handleStep2Next} sx={btnSx}>
                      Next →
                    </Button>
                  </Box>
                </Box>
              )}

              {/* ════ STEP 3 : Center Settings ════ */}
              {activeStep === 2 && (
                <Box>

                  {/* Collection Type */}
                  <FormControl fullWidth sx={inputSx}>
                    <InputLabel>Collection Type</InputLabel>
                    <Select
                      value={collectionTypeId}
                      label="Collection Type"
                      onChange={(e) => setCollectionTypeId(e.target.value)}
                    >
                      <MenuItem value={1}>Cow</MenuItem>
                      <MenuItem value={2}>Buffalo</MenuItem>
                      <MenuItem value={3}>Cow + Buffalo</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Milk Type */}
                  <FormControl fullWidth sx={inputSx}>
                    <InputLabel>Milk Type</InputLabel>
                    <Select
                      value={milkRateTypeId}
                      label="Milk Type"
                      onChange={(e) => setMilkRateTypeId(e.target.value)}
                    >
                      <MenuItem value={1}>FAT only</MenuItem>
                      <MenuItem value={2}>CLR only</MenuItem>
                      <MenuItem value={3}>FAT + SNF</MenuItem>
                      <MenuItem value={4}>FAT + CLR</MenuItem>
                      <MenuItem value={5}>FAT + CLR + Auto SNF</MenuItem>
                      <MenuItem value={6}>Liter Only</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Collection Shift */}
                  <FormControl fullWidth sx={inputSx}>
                    <InputLabel>Collection Shift</InputLabel>
                    <Select
                      value={shiftId}
                      label="Collection Shift"
                      onChange={(e) => setShiftId(e.target.value)}
                    >
                      <MenuItem value={1}>Morning</MenuItem>
                      <MenuItem value={2}>Evening</MenuItem>
                      <MenuItem value={3}>Morning + Evening</MenuItem>
                      <MenuItem value={4}>Auto (Morning + Evening)</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Payment Period */}
                  <FormControl fullWidth sx={inputSx}>
                    <InputLabel>Payment Period</InputLabel>
                    <Select
                      value={paymentPeriodId}
                      label="Payment Period"
                      onChange={(e) => setPaymentPeriodId(e.target.value)}
                    >
                      <MenuItem value={1}>01-10, 11-20, 21-ME</MenuItem>
                      <MenuItem value={2}>01-15, 16-ME</MenuItem>
                      <MenuItem value={3}>01-ME</MenuItem>
                      <MenuItem value={4}>01-05, 06-10, 11-15, 16-20, 21-25, 26-ME</MenuItem>
                      <MenuItem value={5}>06-15, 16-25, 26-05</MenuItem>
                      <MenuItem value={6}>Monday</MenuItem>
                      <MenuItem value={7}>Tuesday</MenuItem>
                      <MenuItem value={8}>Wednesday</MenuItem>
                      <MenuItem value={9}>Thursday</MenuItem>
                      <MenuItem value={10}>Friday</MenuItem>
                      <MenuItem value={11}>Saturday</MenuItem>
                      <MenuItem value={12}>Sunday</MenuItem>
                    </Select>
                  </FormControl>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Button variant="outlined" onClick={() => setActiveStep(1)} sx={backBtnSx}>
                      ← Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleComplete}
                      disabled={loading}
                      sx={btnSx}
                    >
                      {loading
                        ? <CircularProgress size={22} color="inherit" />
                        : '✓ Finish'}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* ════ STEP 4 : Success ════ */}
              {activeStep === 3 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CheckCircle sx={{ fontSize: 80, color: theme.primary, mb: 2 }} />
                  <Typography variant="h5" sx={{
                    fontWeight: 800,
                    color:      theme.primary,
                    fontFamily: 'Poppins, sans-serif',
                    mb:         1
                  }}>
                    Registration Successful! 🎉
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888', mb: 1 }}>
                    Your dairy center has been created successfully.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888', mb: 3 }}>
                    You are on <strong>7 Days Free Trial</strong>. You can now login.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/login')}
                    sx={{ ...btnSx, px: 5 }}
                  >
                    Go to Login →
                  </Button>
                </Box>
              )}

            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
